import React, { useState } from 'react'
import { useFetchCampaigns } from '../../hooks/campaign/useFetchCampaigns'
import { useDeleteCampaign } from 'presentation/hooks/campaign/useDeleteCampaign'
import { DataTable, Column } from '../../components/Datatable/Datatable'
import { Button } from '../../components/Button/Button'
import {
  Campaign,
  CampaignInput,
  CampaignStatus,
} from 'domain/entities/Campaign'
import './CampaignList.css'
import { useUpdateCampaign } from 'presentation/hooks/campaign/useUpdateCampaign'
import ConfirmationModal from 'presentation/components/Modals/ConfirmationModal/ConfirmationModal'

type CampaignListProps = {
  onEdit: (campaign: Campaign) => void
}

export const CampaignList: React.FC<CampaignListProps> = ({ onEdit }) => {
  const { data = [], loading, refetch } = useFetchCampaigns()
  const { deleteCampaign } = useDeleteCampaign()
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleteTitle, setDeleteTitle] = useState<string | null>(null)

  const handleOpenConfirmationModal = (id: string, title: string) => {
    setDeleteId(id)
    setDeleteTitle(title)
    setIsConfirmationModalOpen(true)
  }

  const handleCloseConfirmationModal = () => {
    setDeleteId(null)
    setDeleteTitle(null)
    setIsConfirmationModalOpen(false)
  }

  const handleDelete = async () => {
    if (deleteId) {
      try {
        await deleteCampaign(deleteId)
        refetch()
        handleCloseConfirmationModal()
      } catch (e) {
        console.error(e)
      }
    }
  }

  const { updateCampaign } = useUpdateCampaign()

  const handleToggleStatus = async (c: Campaign) => {
    const newStatus =
      c.status === CampaignStatus.Live
        ? CampaignStatus.Paused
        : CampaignStatus.Live
    await updateCampaign(c.id, { status: newStatus } as CampaignInput)
    refetch()
  }

  if (loading) return <div>Loading…</div>

  if (data.length === 0) return <div>No campaign available</div>

  const columns: Column<Campaign>[] = [
    {
      header: 'Status',
      accessor: (c) => (
        <div
          onClick={() => handleToggleStatus(c)}
          style={{ cursor: 'pointer', width: '100%', height: '100%' }}
          title={
            c.status === CampaignStatus.Live
              ? 'Live: Click to pause the campaign'
              : 'Paused: Click to start the campaign'
          }
        >
          <img
            src={`/icons/campaignStatus/${c.status}.svg`}
            alt={c.status}
            className="campaign-status-icon"
          />
        </div>
      ),
      className: 'status-column',
    },
    { header: 'Title', accessor: 'title', className: 'text-left' },
    {
      header: 'Created At',
      accessor: (c) => new Date(c.createdAt * 1_000).toLocaleDateString(),
    },
    {
      header: 'Actions',
      accessor: (c) => (
        <>
          <Button variant="edit" onClick={() => onEdit(c)}>
            Edit
          </Button>{' '}
          <Button
            variant="delete"
            onClick={() => handleOpenConfirmationModal(c.id, c.title)}
          >
            Delete
          </Button>
        </>
      ),
    },
  ]

  return (
    <>
      <DataTable columns={columns} data={data} rowKey={(c) => c.id} />
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={handleCloseConfirmationModal}
        bodyContent={
          <>
            <p>
              Are you sure you want to permanently delete <b>{deleteTitle}</b>?
              This action cannot be undone.
            </p>
          </>
        }
        footerContent={
          <>
            <Button
              variant="cancel"
              onClick={() => handleCloseConfirmationModal()}
            >
              Cancel
            </Button>
            <Button variant="delete" onClick={() => handleDelete()}>
              Delete
            </Button>
          </>
        }
      />
    </>
  )
}
